import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Volume2, Loader2, Square, Globe, Clock, Check, FileText, BookOpen, ChevronRight, Eye, Download } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import EmergencyButton from '../components/EmergencyButton';
import Footer from '../components/Footer';
import DocumentHeader from '../components/DocumentHeader';
import ProtectedContent from '../components/ProtectedContent';
import { sanitizeHtml, safeHtml } from '../lib/sanitize';

const LANGUAGES = {
  en: 'English', ru: 'Russian', uk: 'Ukrainian', pt: 'Portuguese',
  fr: 'French', it: 'Italian', es: 'Spanish', ko: 'Korean'
};

function LessonViewPage() {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState(user?.language || 'en');
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pdfLang, setPdfLang] = useState(user?.language || 'en');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const audioRef = useRef(null);

  const fetchLesson = useCallback(async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/lessons/${lessonId}`);
      if (response.ok) {
        setLesson(await response.json());
      } else {
        toast.error('Lesson not found');
        navigate('/lessons');
      }
    } catch (error) {
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  }, [lessonId, navigate]);

  useEffect(() => {
    fetchLesson();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [fetchLesson]);

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = sanitizeHtml(html);
    return tmp.textContent || tmp.innerText || '';
  };

  /** Pick the localised lesson body, falling back to English. */
  const getLocalisedContent = () => {
    if (!lesson) return '';
    const lang = (selectedLang || 'en').toLowerCase();
    return lesson[`content_${lang}`] || lesson.content_en || '';
  };

  const handleListen = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    const plainText = stripHtml(getLocalisedContent());
    if (!plainText.trim()) {
      toast.error('No content available to read aloud');
      return;
    }

    setIsGeneratingAudio(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/tts/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: plainText, language: selectedLang, voice: 'onyx' })
      });

      if (response.ok) {
        const data = await response.json();
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audioRef.current = audio;
        audio.onended = () => { setIsPlaying(false); audioRef.current = null; };
        audio.onerror = () => { setIsPlaying(false); audioRef.current = null; toast.error('Audio playback failed'); };
        await audio.play();
        setIsPlaying(true);
      } else {
        toast.error('Failed to generate audio');
      }
    } catch (error) {
      toast.error('Failed to generate audio');
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleComplete = async () => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/lessons/${lessonId}/complete`, {
        method: 'POST'
      });
      toast.success('Lesson completed!');
      setLesson(prev => ({ ...prev, completed: true }));
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!lesson) return null;

  // Build the merged language list: prefer inline content_${lang} (always
  // print-able via /api/lessons/{id}/print), and any pre-uploaded pdf_urls.
  const inlineLangs = (lesson.available_languages || []).filter((l) => lesson[`content_${l}`]);
  const externalPdfLangs = Object.keys(lesson.pdf_urls || {});
  const allDocLangs = Array.from(new Set([...inlineLangs, ...externalPdfLangs]));
  const hasAnyDoc = allDocLangs.length > 0;
  const hasInline = inlineLangs.includes(pdfLang);
  const printUrl = `${process.env.REACT_APP_BACKEND_URL}/api/lessons/${lesson.lesson_id}/print?lang=${pdfLang}`;
  const externalPdfUrl = lesson.pdf_urls?.[pdfLang];

  return (
    <div className="min-h-screen bg-zinc-100 pb-24">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-40 h-16 px-4 flex items-center justify-between">
        <button onClick={() => navigate('/library?tab=lessons')} data-testid="back-to-lessons">
          <ArrowLeft className="w-5 h-5 text-zinc-950" />
        </button>
        <h1 className="text-sm font-semibold text-zinc-950 truncate px-4">{lesson.title}</h1>
        <EmergencyButton />
      </header>

      <main className="pt-20 px-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Lesson meta strip  --  supplementary tags above the document.
              The editorial title sits inside .arp-document below. */}
          <div className="flex items-center gap-3 text-xs text-zinc-300 flex-wrap" data-testid="lesson-meta-strip">
            <span className="flex items-center gap-1 px-2 py-0.5 bg-white/10 rounded-full"><Clock className="w-3 h-3" /> {lesson.estimated_time} min</span>
            {lesson.completed && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-200 rounded-full font-semibold"><Check className="w-3 h-3" /> Completed</span>
            )}
            {lesson.machine_translated?.[selectedLang] && selectedLang !== 'en' && (
              <span
                className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-200 rounded-full text-[10px] font-semibold"
                title="This translation was machine-generated and has not yet been reviewed by a native speaker.  The English version is the authoritative source."
                data-testid="machine-translated-badge"
              >
                <Globe className="w-2.5 h-2.5" />
                Machine translated
              </span>
            )}
          </div>

          {/* Listen Controls */}
          <div className="bg-white rounded-xl p-4" data-testid="tts-controls">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-gray-800">Listen to Lesson</span>
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowLangPicker(!showLangPicker)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700"
                  data-testid="language-picker-btn"
                >
                  <Globe className="w-3 h-3" />
                  {LANGUAGES[selectedLang]}
                </button>
                {showLangPicker && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
                    {Object.entries(LANGUAGES).map(([code, name]) => (
                      <button
                        key={code}
                        onClick={() => { setSelectedLang(code); setShowLangPicker(false); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 ${
                          selectedLang === code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                        }`}
                        data-testid={`lang-${code}`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <Button
              onClick={handleListen}
              disabled={isGeneratingAudio}
              className={`w-full h-12 rounded-lg font-medium text-sm ${
                isPlaying ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              data-testid="listen-btn"
            >
              {isGeneratingAudio ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Audio...</>
              ) : isPlaying ? (
                <><Square className="w-4 h-4 mr-2" /> Stop Listening</>
              ) : (
                <><Volume2 className="w-4 h-4 mr-2" /> Listen in {LANGUAGES[selectedLang]}</>
              )}
            </Button>
            {selectedLang !== 'en' && (
              <p className="text-xs text-blue-600 mt-2 text-center">
                The lesson will be translated to {LANGUAGES[selectedLang]} and read aloud
              </p>
            )}
          </div>

          {/* PDF Documents Section */}
          {hasAnyDoc && (
            <div className="bg-white rounded-xl p-4" data-testid="pdf-documents-section">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-gray-800">PDF Documents</span>
                <span className="ml-auto text-[11px] text-zinc-500 font-medium">
                  {allDocLangs.length} {allDocLangs.length === 1 ? 'language' : 'languages'} available
                </span>
              </div>

              {/* Language Tabs */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {allDocLangs.map((langCode) => (
                  <button
                    key={langCode}
                    onClick={() => setPdfLang(langCode)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      pdfLang === langCode
                        ? 'bg-blue-600 text-white'
                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                    }`}
                    data-testid={`pdf-lang-${langCode}`}
                  >
                    {LANGUAGES[langCode] || langCode.toUpperCase()}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mb-3">
                <Button
                  onClick={() => setShowPdfViewer(!showPdfViewer)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm h-10"
                  data-testid="pdf-view-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPdfViewer ? 'Hide Document' : 'Read in App'}
                </Button>
                <a
                  href={externalPdfUrl || printUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-300 bg-white hover:bg-zinc-50 rounded-md h-10 px-4 text-sm font-medium transition-colors"
                  data-testid="pdf-download-btn"
                >
                  <Download className="w-4 h-4" />
                  Download / Print
                </a>
              </div>

              {/* In-App Viewer: uses the print-friendly endpoint for inline languages
                  (so all 8 translations are viewable) or the original PDF URL if uploaded. */}
              {showPdfViewer && (
                <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="pdf-viewer">
                  {externalPdfUrl ? (
                    <iframe
                      src={`https://docs.google.com/gview?url=${encodeURIComponent(externalPdfUrl)}&embedded=true`}
                      title={`${lesson.title} - ${LANGUAGES[pdfLang] || pdfLang}`}
                      className="w-full bg-zinc-50"
                      style={{ height: '70vh', minHeight: '500px' }}
                    />
                  ) : (
                    <iframe
                      src={printUrl}
                      title={`${lesson.title} - ${LANGUAGES[pdfLang] || pdfLang}`}
                      className="w-full bg-white"
                      style={{ height: '70vh', minHeight: '500px' }}
                    />
                  )}
                </div>
              )}

              <p className="text-xs text-zinc-400 mt-2 text-center">
                {LANGUAGES[pdfLang] || pdfLang} version
                {!externalPdfUrl && hasInline && ': generated from lesson content'}
              </p>
            </div>
          )}

          {/* Lesson Content  --  rendered as an editorial document with the
              same serif layout as ARP readings.  Title, italic subtitle and
              grey eyebrow at the top, then the body in serif.
              Batch 7 items 3 & 4: wrap body in ProtectedContent so users
              cannot select, copy, cut, drag or right-click, and a diagonal
              email watermark sits behind the text. */}
          <div className="arp-document" data-testid="lesson-content-wrapper">
            <DocumentHeader
              title="Axiom Recovery Programme (ARP)"
              subtitle={lesson.title}
              eyebrow={
                lesson.lesson_id === 'lesson-intro' || lesson.lesson_id === 'intro'
                  ? 'Programme  ·  Lesson Introduction'
                  : `Programme  ·  Lesson ${lesson.order || ''}`.trim()
              }
              testIdPrefix="lesson"
              intro={lesson.subtitle ? <span>{lesson.subtitle}</span> : null}
            />
            <ProtectedContent>
              <div
                className="reading-content"
                dangerouslySetInnerHTML={safeHtml(getLocalisedContent())}
                data-testid="lesson-content"
              />
            </ProtectedContent>
            {/* Batch 7 item 5: copyright footer on every lesson screen. */}
            <p
              className="text-xs mt-6 text-center"
              style={{ color: '#444444' }}
              data-testid="lesson-copyright-line"
            >
              © Graeme Tudor. Protected by copyright. Not to be copied or shared.
            </p>
          </div>

          {/* Self-Work CTA for this lesson */}
          {lesson.self_work && (
            <div data-testid="lesson-sw-callout">
              {lesson.self_work.available ? (
                <button
                  onClick={() => navigate(lesson.self_work.route)}
                  className="w-full bg-blue-50 border border-blue-200 rounded-xl p-4 text-left hover:bg-blue-100 transition-colors flex items-center gap-3"
                  data-testid="lesson-sw-open-btn"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-zinc-900">Self-Work for Week {lesson.self_work.week}</p>
                    <p className="text-[11px] text-zinc-600 mt-0.5">
                      {lesson.self_work.question_count} question{lesson.self_work.question_count === 1 ? '' : 's'}  ·  Type or speak your answers.
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                </button>
              ) : (
                <div className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-center" data-testid="lesson-sw-unavailable">
                  <p className="text-[12px] text-zinc-600 leading-relaxed">
                    {lesson.self_work.reason || 'No Self-Work for this lesson.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Mark Complete */}
          {!lesson.completed && (
            <Button
              onClick={handleComplete}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-12 rounded-xl font-semibold"
              data-testid="complete-lesson-btn"
            >
              <Check className="w-4 h-4 mr-2" /> Mark Lesson Complete
            </Button>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default LessonViewPage;
